import React, { useMemo, Suspense, lazy } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Ruler, Map, Receipt, FileText, Box, Image as ImageIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { rasterizeSvg } from './tech-view/exportTechnicalSheetPDF';
import type { LayerV2, FrameCoupling } from '../types';
import TechnicalWallDrawing from './section-view/TechnicalWallDrawing';
import CostsBreakdown from './costs-view/CostsBreakdown';
import PlanView from './plan-view/PlanView';
import TechnicalSheet from './tech-view/TechnicalSheet';
import { buildWallSectionModel } from './section-view/wallModel';
import { useSystemCode } from '../hooks/useSystemCode';
import { useTypologyDetection } from '../hooks/useTypologyDetection';
import { useHasFeature } from '@/hooks/useOrgPlan';
import { useCanWrite } from '@/hooks/useCanWrite';
import { UpsellBanner, WriteBlockedBanner } from '@/components/billing/UpsellTooltip';
import { Lock } from 'lucide-react';
import { formatNumber } from '@/utils/format';

// Lazy: il bundle three.js (~500KB) e' caricato solo quando l'utente
// apre il tab 3D fotorealistico.
const Photorealistic3DView = lazy(() => import('./photoreal-3d/Photorealistic3DView'));

interface VisualizationPanelProps {
  layers: LayerV2[];
  /** Spessore "raw" sommando layer.thickness — informativo, mostrato a parte. */
  totalThicknessMm: number;
  /** Interasse montanti scelto a livello stratigrafia (mm). */
  studSpacingMm: 300 | 400 | 600;
  /** Accoppiamento doppia orditura dichiarato sulla parete. null = automatico. */
  frameCoupling?: FrameCoupling | null;

  /** Nome stratigrafia (passato alla scheda Tecnico per export PDF). */
  stratigraphyName?: string;
  /** Descrizione stratigrafia (export PDF). */
  stratigraphyDescription?: string;
  /** Costo €/m² totale calcolato (per export PDF). */
  totalCostPerSqm?: number;
  /**
   * Dati prestazionali certificati (override degli aggregati dai layer).
   * Se la stratigrafia è certificata, questi sono i valori da test in laboratorio
   * (es. catalogo Saint-Gobain) e hanno priorità sui calcoli automatici.
   */
  certifiedFireResistance?: string | null;
  certifiedAcousticPerformance?: number | null;
  certifiedThermalPerformance?: number | null;
  certifiedWeightPerSqm?: number | null;
}

/**
 * Pannello destro del configuratore: tab Sezione | Pianta | Costi | Tecnico.
 *
 * V2.0-S3: mostra spessore totale + count layer come placeholder vivo.
 * V2.1: vero preview SVG con pattern, profili C reali, viti, quote.
 *
 * Vedi `docs/mockupui.md` §9-§13.
 */
const VisualizationPanel: React.FC<VisualizationPanelProps> = ({
  layers,
  totalThicknessMm,
  studSpacingMm,
  frameCoupling,
  stratigraphyName,
  stratigraphyDescription,
  totalCostPerSqm,
  certifiedFireResistance,
  certifiedAcousticPerformance,
  certifiedThermalPerformance,
  certifiedWeightPerSqm,
}) => {
  const typology = useTypologyDetection(layers);
  const systemCode = useSystemCode({ typology, layers });
  const has3D = useHasFeature('view3d');
  const { canWrite } = useCanWrite();
  // Tab 3D SEMPRE cliccabile: cliccando si vede il banner contestuale
  // (rosso "Riattiva" se cancelled, giallo "Passa a Studio" se Solo).
  // Mostriamo il lucchetto a fianco del nome solo come affordance visiva.
  const lockedHint = !canWrite || !has3D;

  // Singola fonte di verità per lo spessore parete: il modello "wall section"
  // gestisce correttamente il caso isolante-dentro-vano-del-montante (NON somma).
  // Il prop `totalThicknessMm` è la somma raw dei layer.thickness, che invece
  // gonfierebbe lo spessore quando l'isolante è dentro la struttura.
  const wallThicknessMm = useMemo(() => buildWallSectionModel(layers, { frameCoupling }).totalThicknessMm, [layers, frameCoupling]);

  // Aggregati prestazioni per il box riassuntivo in fondo.
  // Stesso pattern di CeilingVisualizationShell (F17.2.11) — fix box prestazioni
  // wall che mostrava sempre '—' (era placeholder hardcoded).
  const totalWeight = useMemo(() => layers.reduce((acc, l) => {
    const w = Number(l.material?.weight_per_sqm ?? 0);
    return acc + (Number.isFinite(w) ? w : 0);
  }, 0), [layers]);

  const avgLambda = useMemo(() => {
    const lambdaLayers = layers.filter(l => Number(l.material?.thermal_conductivity ?? 0) > 0);
    if (lambdaLayers.length === 0) return null;
    const numerator = lambdaLayers.reduce((acc, l) => {
      const lam = Number(l.material?.thermal_conductivity ?? 0);
      const t = Number(l.thickness ?? l.material?.thickness ?? 0);
      return acc + lam * t;
    }, 0);
    const denominator = lambdaLayers.reduce(
      (acc, l) => acc + Number(l.thickness ?? l.material?.thickness ?? 0),
      0,
    );
    return denominator > 0 ? numerator / denominator : null;
  }, [layers]);

  const bestRw = useMemo(() => layers.reduce((acc, l) => {
    const r = Number(l.material?.acoustic_performance ?? 0);
    return r > acc ? r : acc;
  }, 0), [layers]);

  const fireClasses = useMemo(() => Array.from(new Set(
    layers
      .map(l => l.material?.fire_class)
      .filter((c): c is string => typeof c === 'string' && c.trim() !== ''),
  )), [layers]);

  return (
    <div className="space-y-4">
      <Tabs defaultValue="section" className="w-full" data-tour-id="config-visualization-tabs">
        <TabsList className="w-full grid grid-cols-5">
          <TabsTrigger value="section"><Ruler className="h-4 w-4 mr-1.5" /> Sezione</TabsTrigger>
          <TabsTrigger value="iso">
            {lockedHint ? <Lock className="h-3 w-3 mr-1.5" /> : <Box className="h-4 w-4 mr-1.5" />}
            Vista 3D
          </TabsTrigger>
          <TabsTrigger value="plan"><Map className="h-4 w-4 mr-1.5" /> Pianta</TabsTrigger>
          <TabsTrigger value="costs"><Receipt className="h-4 w-4 mr-1.5" /> Costi</TabsTrigger>
          <TabsTrigger value="tech"><FileText className="h-4 w-4 mr-1.5" /> Tecnico</TabsTrigger>
        </TabsList>

        <TabsContent value="section" className="mt-4">
          <div className="flex justify-end mb-2">
            <Button
              size="sm"
              variant="outline"
              disabled={layers.length === 0}
              onClick={async () => {
                const svg = document.querySelector<SVGSVGElement>(
                  '[data-section-view] svg.technical-wall-svg, .technical-wall-svg',
                );
                if (!svg) {
                  toast.error('Sezione non trovata.');
                  return;
                }
                const dataUrl = await rasterizeSvg(svg, 2);
                if (!dataUrl) {
                  toast.error('Errore durante la generazione del PNG.');
                  return;
                }
                const a = document.createElement('a');
                const safe = (stratigraphyName ?? 'stratigrafia')
                  .replace(/[^a-z0-9-_ ]/gi, '_')
                  .trim() || 'stratigrafia';
                a.href = dataUrl;
                a.download = `${safe}-sezione.png`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                toast.success('PNG scaricato.');
              }}
            >
              <ImageIcon className="h-4 w-4 mr-1.5" />
              Esporta PNG
            </Button>
          </div>
          <TechnicalWallDrawing
            layers={layers}
            systemCode={layers.length > 0 ? systemCode : undefined}
            studSpacingMm={studSpacingMm}
            frameCoupling={frameCoupling}
          />
        </TabsContent>

        <TabsContent value="iso" className="mt-4">
          {/* Priorità: canWrite (subscription) > has3D (gate piano).
              Se cancelled mostro banner rosso, se Solo banner giallo, altrimenti rendo. */}
          {!canWrite ? (
            <WriteBlockedBanner
              feature="Vista 3D Fotorealistica"
              description="La visualizzazione 3D resta accessibile solo con abbonamento attivo. Riattiva per usare di nuovo questa funzionalità."
            />
          ) : !has3D ? (
            <UpsellBanner
              plan="studio"
              feature="Vista 3D Fotorealistica"
              description="Render fotorealistico della stratigrafia con texture reali dei materiali (cartongesso, isolante, profili). Disponibile dal piano Studio."
            />
          ) : (
            <Suspense
              fallback={
                <Card className="p-12 text-center text-sm text-muted-foreground border-dashed min-h-[420px] flex items-center justify-center">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/60" />
                    Caricamento motore 3D...
                  </div>
                </Card>
              }
            >
              <Photorealistic3DView
                layers={layers}
                studSpacingMm={studSpacingMm}
                frameCoupling={frameCoupling}
                stratigraphyName={stratigraphyName}
              />
            </Suspense>
          )}
        </TabsContent>

        <TabsContent value="plan" className="mt-4">
          <PlanView layers={layers} studSpacingMm={studSpacingMm} frameCoupling={frameCoupling} />
        </TabsContent>

        <TabsContent value="costs" className="mt-4">
          <CostsBreakdown layers={layers} studSpacingMm={studSpacingMm} />
        </TabsContent>

        <TabsContent value="tech" className="mt-4">
          <TechnicalSheet
            layers={layers}
            studSpacingMm={studSpacingMm}
            frameCoupling={frameCoupling}
            systemCode={layers.length > 0 ? systemCode : undefined}
            typology={typology}
            wallThicknessMm={wallThicknessMm}
            stratigraphyName={stratigraphyName}
            stratigraphyDescription={stratigraphyDescription}
            totalCostPerSqm={totalCostPerSqm}
          />
        </TabsContent>
      </Tabs>

      {/* Box prestazioni — priorità ai dati CERTIFICATI (test lab), fallback su
          aggregati live dai materiali dei layer (stratigrafia personalizzata). */}
      <Card className="p-4">
        <div className="grid grid-cols-4 gap-3 text-center">
          {[
            {
              label: 'EI',
              value: certifiedFireResistance ?? (fireClasses.length > 0 ? fireClasses[0] : '—'),
              sub: 'fuoco',
              certified: !!certifiedFireResistance,
            },
            {
              label: 'Rw',
              value: certifiedAcousticPerformance != null && certifiedAcousticPerformance > 0
                ? `${formatNumber(certifiedAcousticPerformance, 0)} dB`
                : (bestRw > 0 ? `${formatNumber(bestRw, 0)} dB` : '—'),
              sub: 'fonoiso.',
              certified: certifiedAcousticPerformance != null && certifiedAcousticPerformance > 0,
            },
            {
              label: 'λ',
              value: certifiedThermalPerformance != null && certifiedThermalPerformance > 0
                ? formatNumber(certifiedThermalPerformance, 3)
                : (avgLambda !== null ? formatNumber(avgLambda, 3) : '—'),
              sub: certifiedThermalPerformance != null && certifiedThermalPerformance > 0 ? 'W/m²K (U)' : 'termico',
              certified: certifiedThermalPerformance != null && certifiedThermalPerformance > 0,
            },
            {
              label: 'Peso',
              value: certifiedWeightPerSqm != null && certifiedWeightPerSqm > 0
                ? formatNumber(certifiedWeightPerSqm, 1)
                : (totalWeight > 0 ? formatNumber(totalWeight, 1) : '—'),
              sub: 'kg/m²',
              certified: certifiedWeightPerSqm != null && certifiedWeightPerSqm > 0,
            },
          ].map((m) => (
            <div key={m.label}>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center justify-center gap-1">
                {m.label}
                {m.certified && (
                  <span className="text-[7px] text-emerald-600" title="Valore certificato da test di laboratorio">●</span>
                )}
              </div>
              <div className={`text-2xl font-semibold ${m.value === '—' ? 'text-zinc-400' : m.certified ? 'text-emerald-700' : 'text-zinc-700'}`}>{m.value}</div>
              <div className="text-[9px] text-muted-foreground">{m.sub}</div>
            </div>
          ))}
        </div>
        <div className="mt-2 pt-2 border-t flex items-center justify-between text-xs text-muted-foreground">
          <span>Spessore parete</span>
          <span className="font-mono font-medium text-zinc-700">{formatNumber(wallThicknessMm, 1)} mm</span>
        </div>
        {Math.abs(totalThicknessMm - wallThicknessMm) > 0.5 && (
          <div className="flex items-center justify-between text-[10px] text-muted-foreground/70 mt-0.5">
            <span>Somma layer (raw)</span>
            <span className="font-mono">{formatNumber(totalThicknessMm, 1)} mm</span>
          </div>
        )}
      </Card>
    </div>
  );
};

export default VisualizationPanel;
